import React, { useEffect, useState } from "react";
import { Bar } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Tooltip,
  Legend,
} from "chart.js";
import * as S from "./IASistemasStyle";
import { iaService } from "../../Services/iaService";

ChartJS.register(CategoryScale, LinearScale, BarElement, Tooltip, Legend);

const TICKET_STATUS = {
  1: { label: "Pendente", tipo: "pendente" },
  2: { label: "Em análise", tipo: "analise" },
  3: { label: "Resolvido", tipo: "resolvido" },
  4: { label: "Cancelado", tipo: "cancelado" },
};

export default function DashboardTab() {
  const [stats, setStats] = useState(null);
  const [erro, setErro] = useState(null);

  useEffect(() => {
    iaService
      .getDashboardStats()
      .then(setStats)
      .catch((e) => setErro(e?.response?.data?.error || e.message));
  }, []);

  if (erro) return <S.LoadingText>Erro ao carregar: {erro}</S.LoadingText>;
  if (!stats) return <S.LoadingText>Carregando painel...</S.LoadingText>;

  const t = stats.tickets || {};
  const ch = stats.channels_total || {};
  const abertos = (t.pendentes || 0) + (t.em_analise || 0);
  const maxChannel = Math.max(ch.instagram || 0, ch.website || 0, ch.plataforma || 0, 1);

  const chartData = {
    labels: (stats.weekly_performance || []).map((d) =>
      new Date(d.date).toLocaleDateString("pt-BR", { day: "2-digit", month: "2-digit" })
    ),
    datasets: [
      {
        label: "Novos leads",
        data: (stats.weekly_performance || []).map((d) => d.leads_count),
        backgroundColor: "#B17D2A",
        borderRadius: 2,
        maxBarThickness: 42,
      },
    ],
  };

  const chartOptions = {
    responsive: true,
    plugins: { legend: { display: false } },
    scales: {
      x: { ticks: { color: "#8a91a0", font: { size: 11 } }, grid: { display: false } },
      y: {
        ticks: { color: "#8a91a0", precision: 0, font: { size: 11 } },
        grid: { color: "#eef0f3" },
        beginAtZero: true,
      },
    },
  };

  return (
    <>
      <S.SectionTitle>Atendimento — Hoje</S.SectionTitle>
      <S.KpiGrid>
        <S.KpiCard>
          <label>Total de Leads (Hoje)</label>
          <strong>{stats.total_leads_today}</strong>
          <span>
            {stats.instagram_enabled !== false
              ? `Instagram ${stats.instagram_leads_today} · Website ${stats.website_leads_today}`
              : `Website ${stats.website_leads_today}`}
          </span>
        </S.KpiCard>
        <S.KpiCard>
          <label>Leads no Mês</label>
          <strong>{stats.total_leads_this_month}</strong>
          <span>Novas conversas iniciadas</span>
        </S.KpiCard>
        <S.KpiCard>
          <label>Chats Ativos (Hoje)</label>
          <strong>{stats.total_chats_opened_today}</strong>
          <span>Conversas com atividade hoje</span>
        </S.KpiCard>
        <S.KpiCard $accent={abertos > 0 ? "#b54708" : "#067647"}>
          <label>Tickets em Aberto</label>
          <strong>{abertos}</strong>
          <span>
            {t.pendentes || 0} pendentes · {t.em_analise || 0} em análise
          </span>
        </S.KpiCard>
        <S.KpiCard $accent={t.urgentes_em_aberto > 0 ? "#b42318" : "#067647"}>
          <label>Urgentes em Aberto</label>
          <strong>{t.urgentes_em_aberto || 0}</strong>
          <span>{t.novos_hoje || 0} tickets abertos hoje</span>
        </S.KpiCard>
      </S.KpiGrid>

      <S.TwoCols>
        <S.Panel>
          <h3>Novos leads — últimos 7 dias</h3>
          <Bar data={chartData} options={chartOptions} height={110} />
        </S.Panel>

        <S.Panel>
          <h3>Conversas por canal (total)</h3>
          {stats.instagram_enabled !== false && (
            <S.MiniBarRow>
              <div className="label">Instagram</div>
              <div className="track">
                <div className="fill" style={{ width: `${((ch.instagram || 0) / maxChannel) * 100}%` }} />
              </div>
              <div className="value">{ch.instagram || 0}</div>
            </S.MiniBarRow>
          )}
          <S.MiniBarRow>
            <div className="label">Website</div>
            <div className="track">
              <div className="fill" style={{ width: `${((ch.website || 0) / maxChannel) * 100}%` }} />
            </div>
            <div className="value">{ch.website || 0}</div>
          </S.MiniBarRow>
          <S.MiniBarRow>
            <div className="label">Plataforma</div>
            <div className="track">
              <div className="fill" style={{ width: `${((ch.plataforma || 0) / maxChannel) * 100}%` }} />
            </div>
            <div className="value">{ch.plataforma || 0}</div>
          </S.MiniBarRow>

          <h3 style={{ marginTop: 26 }}>Suporte — visão geral</h3>
          <S.MiniBarRow>
            <div className="label">Resolvidos</div>
            <div className="track">
              <div
                className="fill"
                style={{
                  width: `${((t.resolvidos || 0) / Math.max((t.resolvidos || 0) + abertos, 1)) * 100}%`,
                  background: "#067647",
                }}
              />
            </div>
            <div className="value">{t.resolvidos || 0}</div>
          </S.MiniBarRow>
          <S.MiniBarRow>
            <div className="label">Em aberto</div>
            <div className="track">
              <div
                className="fill"
                style={{
                  width: `${(abertos / Math.max((t.resolvidos || 0) + abertos, 1)) * 100}%`,
                  background: "#b54708",
                }}
              />
            </div>
            <div className="value">{abertos}</div>
          </S.MiniBarRow>
        </S.Panel>
      </S.TwoCols>

      <S.Panel>
        <h3>Últimos tickets de suporte</h3>
        {(stats.recent_tickets || []).length === 0 ? (
          <S.LoadingText>Nenhum ticket registrado.</S.LoadingText>
        ) : (
          <div style={{ overflowX: "auto" }}>
            <S.Table>
              <thead>
                <tr>
                  <th>#</th>
                  <th>Cliente</th>
                  <th>Assunto</th>
                  <th>Urgência</th>
                  <th>Status</th>
                  <th>Data</th>
                </tr>
              </thead>
              <tbody>
                {stats.recent_tickets.map((tk) => {
                  const st = TICKET_STATUS[tk.status] || TICKET_STATUS[1];
                  return (
                    <tr key={tk.id}>
                      <td>{tk.id}</td>
                      <td>{tk.client_name || "—"}</td>
                      <td className="titulo">{tk.title || "Sem título"}</td>
                      <td>
                        {tk.emergency_level === "urgente" ? (
                          <S.StatusChip $tipo="urgente">Urgente</S.StatusChip>
                        ) : tk.emergency_level === "prioritario" ? (
                          <S.StatusChip $tipo="pendente">Prioritário</S.StatusChip>
                        ) : (
                          <S.StatusChip $tipo="cancelado">Normal</S.StatusChip>
                        )}
                      </td>
                      <td>
                        <S.StatusChip $tipo={st.tipo}>{st.label}</S.StatusChip>
                      </td>
                      <td>
                        {tk.created_at
                          ? new Date(tk.created_at).toLocaleDateString("pt-BR")
                          : "—"}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </S.Table>
          </div>
        )}
      </S.Panel>
    </>
  );
}
